import { Camera } from "@mediapipe/camera_utils";
import { FaceMesh } from "@mediapipe/face_mesh";

export class FaceCaptureUtil {

    /**
     * Center digital zoom applied when copying the video frame into the capture canvas.
     * Keep in sync with `$camera-display-zoom` in `face-capture.component.scss`.
     */
    private static readonly CAPTURE_ZOOM = 1.12;

    public static getFaceDistance(landmarks) {

        const chin = landmarks[152];
        const forehead = landmarks[10];

        const faceHeight = Math.sqrt(
            Math.pow(forehead[0] - chin[0], 2) +
            Math.pow(forehead[1] - chin[1], 2)
        );

        return faceHeight ? +(faceHeight * 10).toFixed(1) : null
    }

    public static getFaceRotation(landmarks) {
        // Extract important landmarks
        const leftEye = landmarks[33];
        const rightEye = landmarks[263];
        const noseTip = landmarks[1];
        const chin = landmarks[199];

        if (!leftEye || !rightEye || !noseTip || !chin) return

        const eyeCenterX = (leftEye[0] + rightEye[0]) / 2;
        const yaw = noseTip[0] - eyeCenterX;
        const eyeCenterY = (leftEye[1] + rightEye[1]) / 2;
        const pitch = noseTip[1] - eyeCenterY;

        return [pitch * 100, yaw * 1000];
    }

    public static getVisibilityChangeEventName(): string {
        if (typeof document.hidden !== 'undefined') {
            return 'visibilitychange';
        } else if (typeof (document as any).msHidden !== 'undefined') {
            return 'msvisibilitychange';
        } else if (typeof (document as any).webkitHidden !== 'undefined') {
            return 'webkitvisibilitychange';
        } else {
            throw new Error('Page Visibility API is not supported.');
        }
    }

    public static handleVisibilityChange(camera: Camera): void {
        if (document.hidden) {
            camera.stop();
        } else {
            camera.start();
        }
    }

    /** Pauses/resumes the MediaPipe camera when the tab is hidden/shown. Caller must remove when capture is done. */
    public static attachCameraVisibilityListener(camera: Camera): () => void {
        const visibilityHandler = () => FaceCaptureUtil.handleVisibilityChange(camera);
        const eventName = FaceCaptureUtil.getVisibilityChangeEventName();
        document.addEventListener(eventName, visibilityHandler, false);
        return () => {
            document.removeEventListener(eventName, visibilityHandler, false);
        };
    }

    /**
     * @param getFaceMesh Live getter so the camera loop can keep running across
     * silent FaceMesh swaps (no-face recovery) without restarting the video.
     */
    public static startCamera(
        getFaceMesh: () => FaceMesh | null,
        videoElement: HTMLVideoElement
    ) {
        const camera = new Camera(videoElement, {
            onFrame: async () => {
                const faceMesh = getFaceMesh();
                if (!faceMesh) {
                    return;
                }
                try {
                    await faceMesh.send({ image: videoElement });
                } catch (error) {
                    console.warn('Skipping failed FaceMesh frame:', error);
                }
            },
            width: 1280,
            height: 720,
        });

        camera.start();

        const cleanup = FaceCaptureUtil.attachCameraVisibilityListener(camera);

        return {
            camera,
            cleanup,
        };
    }

    public static captureCameraFeed({ videoElement, cameraResultCanvas }): string | null {
        const vw = videoElement.videoWidth;
        const vh = videoElement.videoHeight;
        if (!vw || !vh) {
            return null;
        }

        const z = FaceCaptureUtil.CAPTURE_ZOOM;
        const sw = vw / z;
        const sh = vh / z;
        const sx = (vw - sw) / 2;
        const sy = (vh - sh) / 2;

        const context = cameraResultCanvas.nativeElement.getContext('2d');
        cameraResultCanvas.nativeElement.width = vw;
        cameraResultCanvas.nativeElement.height = vh;
        context.setTransform(1, 0, 0, 1, 0, 0);
        context.clearRect(0, 0, vw, vh);
        context.drawImage(videoElement, sx, sy, sw, sh, 0, 0, vw, vh);
        return cameraResultCanvas.nativeElement.toDataURL('image/png');
    }

}
